import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * MotorLand: Consors-Ratenrechner als PDP-Popup (blocks/ml-finanzierungszeile.liquid,
 * Verhalten „popup“).
 *
 * Port nach dem Vorbild des Schwester-Shops E-BIKE ONLY (assets/ebo-consors-rechner.js
 * im Repo MLEBO-GROUP/e-bike-only-horizon) — dort wiederum Port des alten
 * Impulse-Theme-Modals (consors.js / consors-calc.liquid). Die Zins-MATHEMATIK ist
 * 1:1 übernommen — Änderungen an Konditionen oder Formel NUR mit Freigabe von
 * Consors Finanz (PAngV):
 *
 *   Sollzins (nominal, jährl., gebunden) aus dem effektiven Jahreszins:
 *     sollzins = ((1 + eff)^(1/12) − 1) × 12
 *   Monatsrate (Annuität, monatlicher Faktor q = (1 + eff)^(1/12)):
 *     rate = betrag × q^n / (q^n − 1) × (q − 1)        — bei 0 %: betrag / n
 *   Laufzeiten mit einer Rate unter der Mindestrate (9 €) entfallen.
 *
 * Die Konditionen (Laufzeiten, effektiver Jahreszins, Mindestrate) kommen als
 * data-Attribute aus dem Liquid-Block (dort aus den Block-Settings) — hier steht
 * KEINE Kondition hartkodiert. Preisbasis bei MotorLand: Variantenpreis PLUS
 * Frachtkosten (Metafield costs.frachtkosten), Parität zum alten Live-Shop.
 *
 * Morph-Sicherheit: Horizon rendert die PDP-Blöcke bei Varianten- und
 * Combined-Listing-Wechseln per Section-Morph neu. Deshalb werden Trigger, Dialog,
 * Preis und Konditionen bei JEDEM Klick bzw. Rendern FRISCH aus dem DOM gelesen —
 * nichts davon wird über Ereignisse hinweg gecacht. Klicks laufen über Delegation
 * auf dem Custom Element (das Element selbst überlebt den Morph, seine Kinder
 * eventuell nicht). Nach einem Variantenwechsel (StandardEvents.productSelect,
 * Promise löst NACH dem Morph auf) werden Teaser-Rate und — falls offen — die
 * Ratentabelle gegen den frischen DOM-Stand neu aufgebaut.
 *
 * @extends {Component}
 */
class MlConsorsRechner extends Component {
  /** @type {Element | null} */
  #section = null;

  #geldFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

  #prozentFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.shopify-section, dialog');
    this.addEventListener('click', this.#onClick);
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);

    // Initialer Abgleich: die Liquid-gerenderte „ab“-Rate (Näherung ohne Zins) wird
    // durch die günstigste ECHTE Rate aus dem Ratenplan ersetzt.
    this.#syncTeaser();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /** @returns {HTMLDialogElement | null} Der Dialog wird bei jedem Zugriff frisch aufgelöst. */
  #getDialog() {
    return this.querySelector('dialog[data-ml-consors-dialog]');
  }

  /**
   * Klick-Delegation statt direkter Listener: ein Section-Morph kann Trigger und
   * Dialog ersetzen, direkte Listener auf den Kindern wären danach tot.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const dialog = this.#getDialog();
    if (!dialog) return;

    if (target.closest('[data-ml-consors-schliessen]')) {
      dialog.close();
      return;
    }

    // Klick auf das ::backdrop: das Event-Target ist das <dialog> selbst.
    if (dialog.open && target === dialog) {
      dialog.close();
      return;
    }

    if (target.closest('a[data-ml-consors-trigger]')) {
      // Ohne JS bleibt der Link der Fallback zur Finanzierungsseite;
      // mit JS öffnet er den Ratenplan.
      event.preventDefault();
      this.#renderRatenplan(dialog);
      dialog.showModal();
    }
  };

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #onProductSelect = (event) => {
    // Auswahl von Produktkarten anderswo auf der Seite ignorieren.
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    // Das Promise löst auf, NACHDEM der Section-Morph den Block (Preis-Attribute,
    // Teaser, Dialog-Inhalt) neu gerendert hat — erst dann neu rechnen.
    event.promise.then(this.#afterMorph).catch(this.#afterMorph);
  };

  #afterMorph = () => {
    this.#syncTeaser();
    const dialog = this.#getDialog();
    // Ist der Dialog während des Wechsels offen, die Tabelle gegen den frischen
    // Preis neu aufbauen (der Morph kann den von JS gefüllten tbody geleert haben).
    if (dialog?.open) this.#renderRatenplan(dialog);
  };

  /**
   * Liest Preis und Konditionen FRISCH aus den data-Attributen des Dialogs.
   * Nie cachen — nach einem Morph gelten Preis/Konditionen des neuen Produkts.
   * @param {HTMLDialogElement} dialog
   * @returns {{ betrag: number, apr: number, laufzeiten: number[], minRate: number } | null}
   */
  #getKonditionen(dialog) {
    const cents = Number.parseInt(dialog.dataset.preisCents ?? '', 10);
    const effZins = Number.parseFloat((dialog.dataset.effZins ?? '').replace(',', '.'));
    const minRate = Number.parseFloat((dialog.dataset.minRate ?? '9').replace(',', '.'));
    const laufzeiten = (dialog.dataset.laufzeiten ?? '')
      .split(',')
      .map((teil) => Number.parseInt(teil.trim(), 10))
      .filter((monate) => Number.isFinite(monate) && monate > 0);

    if (!Number.isFinite(cents) || cents <= 0 || !Number.isFinite(effZins) || !laufzeiten.length) {
      return null;
    }

    return {
      betrag: cents / 100,
      apr: effZins / 100,
      laufzeiten,
      minRate: Number.isFinite(minRate) ? minRate : 9,
    };
  }

  /**
   * Ratenangebote je Laufzeit — Formeln 1:1 aus dem alten consors.js (s. Kopfkommentar).
   * @param {{ betrag: number, apr: number, laufzeiten: number[], minRate: number }} konditionen
   * @returns {{ monate: number, rate: number, gesamt: number, sollzins: number }[]}
   */
  #berechneAngebote({ betrag, apr, laufzeiten, minRate }) {
    const sollzins = apr === 0 ? 0 : (Math.pow(1 + apr, 1 / 12) - 1) * 12;
    const angebote = [];

    for (const monate of laufzeiten) {
      let rate;
      if (apr === 0) {
        rate = betrag / monate;
      } else {
        const q = Math.pow(1 + apr, 1 / 12);
        const qHochN = Math.pow(q, monate);
        rate = ((betrag * qHochN) / (qHochN - 1)) * (q - 1);
      }
      // Mindestrate wie im alten Shop (minPossibleRepayment = 9 €).
      if (rate < minRate) continue;

      angebote.push({ monate, rate, gesamt: rate * monate, sollzins });
    }

    return angebote;
  }

  /**
   * Baut Nettodarlehensbetrag + Ratentabelle im Dialog auf — bei jedem Öffnen neu.
   * @param {HTMLDialogElement} dialog
   */
  #renderRatenplan(dialog) {
    const konditionen = this.#getKonditionen(dialog);
    const tbody = dialog.querySelector('[data-ml-consors-tbody]');
    const betragZiel = dialog.querySelector('[data-ml-consors-betrag]');
    if (!konditionen || !tbody) return;

    if (betragZiel) betragZiel.textContent = this.#geldFormat.format(konditionen.betrag);

    const angebote = this.#berechneAngebote(konditionen);
    tbody.textContent = '';

    for (const angebot of angebote) {
      const zeile = document.createElement('tr');
      const zellen = [
        `${angebot.monate} Monate`,
        this.#geldFormat.format(angebot.rate),
        this.#geldFormat.format(angebot.gesamt),
        `${this.#prozentFormat.format(konditionen.apr * 100)} %`,
        `${this.#prozentFormat.format(angebot.sollzins * 100)} %`,
      ];
      for (const inhalt of zellen) {
        const zelle = document.createElement('td');
        zelle.textContent = inhalt;
        zeile.appendChild(zelle);
      }
      tbody.appendChild(zeile);
    }
  }

  /**
   * Ersetzt die Liquid-Näherung „ab X,– / Monat“ durch die günstigste echte Rate
   * aus dem Ratenplan (im alten Theme zeigte der Teaser dieselbe Rechnung wie das
   * Modal). Format wie im alten consors.js: volle Euro „X,–“, sonst „X,YY“.
   */
  #syncTeaser() {
    const dialog = this.#getDialog();
    const teaser = this.querySelector('[data-ml-consors-teaser]');
    if (!dialog || !teaser) return;

    const konditionen = this.#getKonditionen(dialog);
    if (!konditionen) return;

    const angebote = this.#berechneAngebote(konditionen);
    if (!angebote.length) return;

    const minRate = Math.min(...angebote.map((angebot) => angebot.rate));
    const euro = Math.floor(minRate);
    const centAnteil = Math.floor((minRate - euro) * 100);
    const centText = centAnteil < 1 ? '–' : String(centAnteil).padStart(2, '0');

    teaser.textContent = `ab ${euro},${centText} / Monat`;
  }
}

if (!customElements.get('ml-consors-rechner')) {
  customElements.define('ml-consors-rechner', MlConsorsRechner);
}
